import { SearchOutlined } from "@ant-design/icons"
import { Flex, Radio, Space } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { memo } from "react"
import { useTranslation } from "react-i18next"

import Icon from "../../../../Icon"
import { DateRangeFilter } from "../../../../components/Filters/DateRangeFilter.tsx"
import { DebouncedInput } from "../../../../components/Input/DebouncedInput.tsx"
import SelectComponent from "../../../../components/Select"
import { type ProjectFilters } from "../../redux/projects.types.ts"

interface ProjectListTableFiltersProps {
  filters: ProjectFilters
  filterOptions: Record<string, BaseOptionType[]>
  archived?: boolean
  onFilersChange: (data: Partial<ProjectFilters>) => void
}

export const ProjectListTableFilters = memo(
  ({
    filters,
    filterOptions,
    archived = false,
    onFilersChange
  }: ProjectListTableFiltersProps) => {
    const { t } = useTranslation()

    const onFilterChange = (key: keyof ProjectFilters) => (value: unknown) => {
      onFilersChange({ [key]: value || null })
    }

    return (
      <Flex
        wrap="wrap"
        gap="small"
        justify={"space-between"}
        className="py-4 md:p-4 border-b border-gray-200"
      >
        {/* Four fixed-width controls exceed 1000px, and Space does not wrap
            unless told to — the row would scroll sideways on a laptop. */}
        <Space wrap>
          {archived ? (
            // Two options, so radio buttons rather than a dropdown; the values
            // come from the backend rather than being mirrored by hand here.
            <Radio.Group
              value={filters?.archive_reason ?? null}
              onChange={(event) => onFilterChange("archive_reason")(event.target.value)}
              optionType="button"
              buttonStyle="solid"
              aria-label={t("projectArchive.reasonFilter")}
              options={[
                { label: t("filters.all"), value: null },
                ...(filterOptions?.["archive_reason"] ?? []).map((option) => ({
                  label: String(option.label),
                  value: Number(option.value)
                }))
              ]}
            />
          ) : (
            <SelectComponent
              maxTagCount={"responsive"}
              mode={"multiple"}
              placeholder="Filter by status"
              value={filters?.project_status}
              onChange={onFilterChange("project_status")}
              className={"max-md:basis-60 w-60 max-md:grow"}
              prefix={<Icon type="Filter" className={"text-gray-500"} />}
              allowClear
              options={filterOptions?.["project_status"]}
              optionFilterType="filter"
            />
          )}

          <SelectComponent
            showSearch
            maxTagCount={"responsive"}
            mode={"multiple"}
            placeholder="Filter by manager(s)"
            value={filters?.project_manager}
            onChange={onFilterChange("project_manager")}
            className={"max-md:basis-60 w-72 max-md:grow"}
            prefix={<Icon type="Users" className={"text-gray-600"} />}
            allowClear
            options={filterOptions?.["project_manager"]}
            optionFilterType="filter"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />

          <DebouncedInput
            prefix={<SearchOutlined className={"text-gray-500"} />}
            placeholder={t("filters.customerPlaceholder")}
            value={filters?.customer_name}
            className={"w-60"}
            onValueChange={(value) => onFilersChange({ customer_name: value })}
          />

          {archived ? (
            <DateRangeFilter
              after={filters?.archived_at_after}
              before={filters?.archived_at_before}
              afterKey="archived_at_after"
              beforeKey="archived_at_before"
              placeholder={t("filters.archivedOnPlaceholder")}
              onChange={onFilersChange}
            />
          ) : (
            <DateRangeFilter
              after={filters?.end_date_after}
              before={filters?.end_date_before}
              afterKey="end_date_after"
              beforeKey="end_date_before"
              placeholder={t("filters.dueDatePlaceholder")}
              onChange={onFilersChange}
            />
          )}
        </Space>

        {/*<Space>*/}
        {/*  <Tooltip title={"Import data"}>*/}
        {/*    <Button disabled icon={<Icon type={"Upload"} />} />*/}
        {/*  </Tooltip>*/}
        {/*  <Tooltip title={"Export data"}>*/}
        {/*    <Button disabled icon={<Icon type={"Download"} />} />*/}
        {/*  </Tooltip>*/}
        {/*</Space>*/}
      </Flex>
    )
  }
)

ProjectListTableFilters.displayName = "ProjectListTableFilters"
